import React, { useState, useEffect, useRef } from "react";
import { getSupabaseClient } from '../../lib/supabase';

interface Resource {
  id: string;
  name: string;
  description: string | null;
  author: string | null;
  category: string | null;
  tags: string[] | null;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string | null;
  mime_type: string;
  downloads: number;
  uploaded_at: string;
  uploaded_by: string | null;
  thumbnail_url: string | null;
  content_url: string | null;
  is_active: boolean;
  seeker_viewable: boolean | null;
  admin_public: boolean;
}

const RESOURCE_TYPES = ['All Types', 'PDF', 'Video', 'Document', 'Audio', 'Presentation'];

const getFileType = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (!ext) return 'Document';
  if (ext === 'pdf') return 'PDF';
  if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext)) return 'Video';
  if (['mp3', 'wav', 'aac', 'flac'].includes(ext)) return 'Audio';
  if (['ppt', 'pptx', 'key'].includes(ext)) return 'Presentation';
  if (['doc', 'docx', 'pages', 'rtf'].includes(ext)) return 'Document';
  return ext.toUpperCase();
};

const sanitizeFileName = (name: string) => name.replace(/[^a-zA-Z0-9._-]/g, '_');

const buildObjectKey = (uploaderId: string | null, originalName: string) => {
  const prefix = uploaderId || 'admin';
  return `${prefix}/${Date.now()}-${sanitizeFileName(originalName)}`;
};

export default function ResourcePlaceholderPage() {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('All Types');
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const itemsPerPage = 5;
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    author: '',
    category: '',
    tags: '',
    is_active: true,
    seeker_viewable: false,
    admin_public: true
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editFile, setEditFile] = useState<File | null>(null);

  const supabase = getSupabaseClient();

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      const resourcesData = data || [];

      const uploaderIds = Array.from(
        new Set(
          resourcesData
            .map(resource => resource.uploaded_by)
            .filter((value): value is string => Boolean(value))
        )
      );

      const roleLookup = new Map<string, string>();

      if (uploaderIds.length > 0) {
        const { data: uploaderProfiles, error: profileError } = await supabase
          .from('profiles')
          .select('id, role')
          .in('id', uploaderIds);

        if (profileError) {
          console.error('Error loading uploader roles:', profileError);
        } else {
          uploaderProfiles?.forEach(profile => {
            roleLookup.set(profile.id, profile.role);
          });
        }
      }

      const filtered = resourcesData.filter(resource => {
        if (!resource.uploaded_by) return true;
        const role = roleLookup.get(resource.uploaded_by);
        return role === 'admin';
      });

      setResources(filtered.map((resource) => {
        const publicUrl = supabase.storage.from('resources').getPublicUrl(resource.file_path).data.publicUrl;
        return {
          ...resource,
          content_url: resource.content_url ?? publicUrl
        } as Resource;
      }));
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (!files.length) return;

    setUploading(true);
    try {
      const env = (window as any).__ENV__;
      if (!env?.EDGE_BASE) {
        throw new Error('Missing EDGE_BASE configuration');
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (!accessToken) {
        throw new Error('You must be signed in to upload resources');
      }

      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
        formData.append('category', getFileType(file.name));

        const response = await fetch(`${env.EDGE_BASE}/upload-resource`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(`Upload failed: ${errorBody || response.statusText}`);
        }
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await loadResources();
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error uploading files. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openEditModal = (resource: Resource) => {
    setEditError(null);
    setEditingResource(resource);
    setEditForm({
      name: resource.name ?? '',
      description: resource.description ?? '',
      author: resource.author ?? '',
      category: resource.category ?? '',
      tags: resource.tags?.join(', ') ?? '',
      is_active: resource.is_active,
      seeker_viewable: resource.seeker_viewable ?? false,
      admin_public: resource.admin_public
    });
    setEditFile(null);
  };

  const closeEditModal = () => {
    if (editSaving) return;
    setEditingResource(null);
    setEditError(null);
    setEditForm({
      name: '',
      description: '',
      author: '',
      category: '',
      tags: '',
      is_active: true,
      seeker_viewable: false,
      admin_public: true
    });
    setEditFile(null);
  };

  const handleEditFieldChange = (
    field: 'name' | 'description' | 'author' | 'category' | 'tags' | 'is_active' | 'seeker_viewable' | 'admin_public',
    value: string | boolean
  ) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateResource = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingResource) return;

    const trimmedName = editForm.name.trim();
    if (!trimmedName) {
      setEditError('Name is required.');
      return;
    }

    setEditSaving(true);
    setEditError(null);

    const parsedTags = editForm.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    const payload: Record<string, any> = {
      name: trimmedName,
      description: editForm.description.trim() || null,
      author: editForm.author.trim() || null,
      category: editForm.category.trim() || null,
      tags: parsedTags.length ? parsedTags : null,
      is_active: editForm.is_active,
      seeker_viewable: editForm.seeker_viewable,
      admin_public: editForm.admin_public
    };

    let uploadedFilePath: string | null = null;
    let newFilePublicUrl: string | null = null;

    try {
      if (editFile) {
        const objectKey = buildObjectKey(editingResource.uploaded_by, editFile.name);
        const { error: uploadError } = await supabase.storage
          .from('resources')
          .upload(objectKey, editFile, {
            cacheControl: '3600',
            upsert: false,
            contentType: editFile.type || 'application/octet-stream'
          });

        if (uploadError) {
          throw new Error(`Failed to upload replacement file: ${uploadError.message}`);
        }

        uploadedFilePath = objectKey;
        payload.file_path = objectKey;
        payload.file_name = editFile.name;
        payload.file_size = editFile.size;
        payload.mime_type = editFile.type || 'application/octet-stream';
        payload.file_type = getFileType(editFile.name);
        newFilePublicUrl = supabase.storage.from('resources').getPublicUrl(objectKey).data.publicUrl;
        payload.content_url = newFilePublicUrl;
      }

      const { data, error } = await supabase
        .from('resources')
        .update(payload)
        .eq('id', editingResource.id)
        .select('*')
        .single();

      if (error || !data) {
        throw error || new Error('Failed to update resource');
      }

      await loadResources();
      if (uploadedFilePath && editingResource.file_path && editingResource.file_path !== uploadedFilePath) {
        await supabase.storage.from('resources').remove([editingResource.file_path]);
      }
      setEditingResource(null);
      setEditForm({
        name: '',
        description: '',
        author: '',
        category: '',
        tags: '',
        is_active: true,
        seeker_viewable: false,
        admin_public: true
      });
      setEditFile(null);
    } catch (error) {
      if (uploadedFilePath) {
        await supabase.storage.from('resources').remove([uploadedFilePath]).catch(() => undefined);
      }
      console.error('Error updating resource:', error);
      setEditError(error instanceof Error ? error.message : 'Failed to update resource');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDownload = async (resource: Resource) => {
    try {
      const { data, error } = await supabase.storage
        .from('resources')
        .download(resource.file_path);

      if (error) throw error;

      // Update download count
      await supabase
        .from('resources')
        .update({ downloads: (resource.downloads ?? 0) + 1 })
        .eq('id', resource.id);

      // Create download link
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = resource.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Refresh resources to update download count
      loadResources();
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  const handleDelete = async (resource: Resource) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;

    try {
      // Delete from storage
      await supabase.storage
        .from('resources')
        .remove([resource.file_path]);

      // Delete from database
      await supabase
        .from('resources')
        .delete()
        .eq('id', resource.id);

      await loadResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Error deleting resource. Please try again.');
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.file_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'All Types' || (resource.file_type || getFileType(resource.file_name)) === selectedType;
    return matchesSearch && matchesType;
  });

  const totalPages = Math.ceil(filteredResources.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResources = filteredResources.slice(startIndex, startIndex + itemsPerPage);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFileIcon = (fileType?: string | null) => {
    const type = (fileType || "Document").toUpperCase();
    const baseClass =
      "flex h-10 w-10 items-center justify-center rounded-xl text-xs font-semibold tracking-wide text-[#FEF6D6]";

    switch (type) {
      case "PDF":
        return <div className={`${baseClass} bg-[#B24E4E]`}>PDF</div>;
      case "VIDEO":
        return <div className={`${baseClass} bg-[#403851]`}>VID</div>;
      case "AUDIO":
        return <div className={`${baseClass} bg-[#4F6F6C]`}>AUD</div>;
      case "PRESENTATION":
        return <div className={`${baseClass} bg-[#8C6B44]`}>PPT</div>;
      case "DOC":
      case "DOCX":
      case "DOCUMENT":
        return <div className={`${baseClass} bg-[#2C2731]`}>DOC</div>;
      default:
        return <div className={`${baseClass} bg-[#6F5E7A]`}>{type.slice(0, 4)}</div>;
    }
  };

  return (
    <div className="min-h-full bg-[#FFFCEF] text-[#2C2731] p-6 space-y-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-medium text-[#2C2731] font-recoleta">Resources Management</h1>
          <p className="text-sm text-[#2C2731]/80 md:text-base">
            Upload, curate, and manage content shared across the collective
          </p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2.5 text-sm font-medium text-[#FEF6D6] shadow-md transition-colors hover:bg-[#3C3450] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          {uploading ? 'Uploading...' : 'Upload Resource'}
        </button>
      </header>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
        className="hidden"
        accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4,.mp3,.wav"
      />

      {/* Upload Area */}
      <div
        className="rounded-2xl border-2 border-dashed border-[#2C2731]/40 bg-[#F2EAC3] p-8 text-center transition-colors hover:border-[#2C2731]/60"
        onDrop={(e) => {
          e.preventDefault();
          handleFileUpload(e.dataTransfer.files);
        }}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        role="button"
      >
        <div className="text-[#2C2731]/60 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        </div>
        <p className="text-lg font-medium text-[#2C2731]">Drop files here to upload</p>
        <p className="text-sm text-[#2C2731]/70">or click to browse files</p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
          className="mt-4 inline-flex items-center justify-center rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
        >
          Choose Files
        </button>
        <p className="text-xs text-[#2C2731]/60 mt-2">Supports: PDF, DOC, DOCX, PPT, PPTX, MP4, MP3 (Max 50MB)</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 rounded-xl border border-[#2C2731] bg-[#2C2731] p-4 text-[#FEF6D6] shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div className="text-lg font-medium font-recoleta">All Resources</div>
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <div className="relative w-full sm:w-80">
            <input
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-[#FEF6D6] bg-[#FFFCEF] px-3 py-2.5 pl-9 text-sm text-[#2C2731] placeholder-[#2C2731]/50 focus:border-[#F2EAC3] focus:outline-none focus:ring-2 focus:ring-[#F2EAC3]"
            />
            <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="rounded-lg border border-[#FEF6D6] bg-[#FFFCEF] px-3 py-2.5 text-sm text-[#2C2731] focus:border-[#F2EAC3] focus:outline-none focus:ring-2 focus:ring-[#F2EAC3]"
          >
            {RESOURCE_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Resources Table */}
      <div className="overflow-hidden rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-[#2C2731] text-[#FEF6D6]">
            <tr>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">Resource</th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">Type</th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">Size</th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">Downloads</th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">Uploaded</th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">Status</th>
              <th className="px-4 py-3 text-right font-medium uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-sm text-[#2C2731]/70">Loading resources…</td>
              </tr>
            ) : paginatedResources.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-sm text-[#2C2731]/70">No resources found. Adjust filters or upload something new.</td>
              </tr>
            ) : (
              paginatedResources.map((resource) => {
                const resourceType = resource.file_type || getFileType(resource.file_name);
                return (
                  <tr key={resource.id} className="bg-[#FFFCEF] text-[#2C2731] transition hover:bg-[#F2EAC3]">
                    <td className="px-4 py-4 align-top">
                      <div className="flex items-start gap-3">
                        {getFileIcon(resourceType)}
                        <div>
                          <div className="text-sm font-medium text-[#2C2731]">{resource.name}</div>
                          <div className="text-xs text-[#2C2731]/70">{resource.file_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top text-sm">{resourceType}</td>
                    <td className="px-4 py-4 align-top text-sm">{formatFileSize(resource.file_size)}</td>
                    <td className="px-4 py-4 align-top text-sm">{resource.downloads}</td>
                    <td className="px-4 py-4 align-top text-sm">{formatDate(resource.uploaded_at)}</td>
                    <td className="px-4 py-4 align-top text-sm">
                    <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                          resource.is_active
                            ? 'bg-[#2C2731] text-[#FEF6D6]'
                            : 'bg-[#F2EAC3] text-[#2C2731]/70'
                        }`}
                      >
                        {resource.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-top text-right text-sm">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditModal(resource)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#2C2731]/20 bg-[#FFFCEF] text-[#2C2731] transition-colors hover:border-[#2C2731]/60 hover:bg-[#F2EAC3]"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232a2.5 2.5 0 113.536 3.536L7.5 20.036 3 21l.964-4.5 11.268-11.268z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDownload(resource)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#2C2731]/20 bg-[#FFFCEF] text-[#2C2731] transition-colors hover:border-[#2C2731]/60 hover:bg-[#F2EAC3]"
                          title="Download"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(resource)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#B24E4E]/30 bg-[#FFFCEF] text-[#B24E4E] transition-colors hover:border-[#B24E4E]/60 hover:bg-[#FCEDEA]"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col gap-3 text-sm text-[#2C2731]/80 md:flex-row md:items-center md:justify-between">
          <div>
            Showing {startIndex + 1}–
            {Math.min(startIndex + itemsPerPage, filteredResources.length)} of{" "}
            {filteredResources.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`rounded-lg border border-[#2C2731] px-3 py-2 font-medium transition-colors ${
                currentPage === 1
                  ? 'cursor-not-allowed bg-[#F2EAC3] text-[#2C2731]/40'
                  : 'bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450]'
              }`}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`rounded-lg border border-[#2C2731] px-3 py-2 text-sm font-medium transition-colors ${
                  currentPage === page
                    ? 'bg-[#2C2731] text-[#FEF6D6]'
                    : 'bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F2EAC3]'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`rounded-lg border border-[#2C2731] px-3 py-2 font-medium transition-colors ${
                currentPage === totalPages
                  ? 'cursor-not-allowed bg-[#F2EAC3] text-[#2C2731]/40'
                  : 'bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450]'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editingResource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2731]/70 px-4 py-12">
          <div className="w-full max-w-2xl rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 text-[#2C2731] shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-medium font-recoleta text-[#2C2731]">Edit Resource</h2>
                <p className="text-sm text-[#2C2731]/70">Update details for "{editingResource.name}"</p>
              </div>
              <button
                type="button"
                onClick={closeEditModal}
                className="text-[#2C2731]/50 transition hover:text-[#2C2731]"
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {editError && (
              <div className="mt-4 rounded-xl border border-[#B24E4E] bg-[#FCEDEA] px-3 py-2 text-sm text-[#B24E4E]">
                {editError}
              </div>
            )}

            <form onSubmit={handleUpdateResource} className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-[#2C2731]/80" htmlFor="resource-name">
                  Name
                </label>
                <input
                  id="resource-name"
                  type="text"
                  value={editForm.name}
                  onChange={event => handleEditFieldChange('name', event.target.value)}
                  className="w-full rounded-lg border border-[#2C2731]/20 bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="Resource name"
                  disabled={editSaving}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-[#2C2731]/80" htmlFor="resource-description">
                  Description
                </label>
                <textarea
                  id="resource-description"
                  value={editForm.description}
                  onChange={event => handleEditFieldChange('description', event.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-[#2C2731]/20 bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="Brief description"
                  disabled={editSaving}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-[#2C2731]/80" htmlFor="resource-author">
                    Author
                  </label>
                  <input
                    id="resource-author"
                    type="text"
                    value={editForm.author}
                    onChange={event => handleEditFieldChange('author', event.target.value)}
                    className="w-full rounded-lg border border-[#2C2731]/20 bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                    placeholder="Resource author"
                    disabled={editSaving}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-[#2C2731]/80" htmlFor="resource-category">
                    Category
                  </label>
                  <input
                    id="resource-category"
                    type="text"
                    value={editForm.category}
                    onChange={event => handleEditFieldChange('category', event.target.value)}
                    className="w-full rounded-lg border border-[#2C2731]/20 bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                    placeholder="Category"
                    disabled={editSaving}
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-[#2C2731]/80" htmlFor="resource-tags">
                  Tags (comma separated)
                </label>
                <input
                  id="resource-tags"
                  type="text"
                  value={editForm.tags}
                  onChange={event => handleEditFieldChange('tags', event.target.value)}
                  className="w-full rounded-lg border border-[#2C2731]/20 bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="e.g. Breathing, Meditation"
                  disabled={editSaving}
                />
              </div>

              <div className="flex items-center justify-between rounded-xl border border-[#2C2731]/15 bg-[#F2EAC3] px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[#2C2731]">Visibility</p>
                  <p className="text-xs text-[#2C2731]/70">Inactive resources are hidden from guides and seekers.</p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-[#2C2731]">
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={event => handleEditFieldChange('is_active', event.target.checked)}
                    disabled={editSaving}
                    className="h-4 w-4 accent-[#2C2731]"
                  />
                  Active
                </label>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-[#2C2731]/15 bg-[#F2EAC3] px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[#2C2731]">Seeker Access</p>
                  <p className="text-xs text-[#2C2731]/70">Allow seekers to view this resource.</p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-[#2C2731]">
                  <input
                    type="checkbox"
                    checked={editForm.seeker_viewable}
                    onChange={event => handleEditFieldChange('seeker_viewable', event.target.checked)}
                    disabled={editSaving}
                    className="h-4 w-4 accent-[#2C2731]"
                  />
                  Viewable
                </label>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-[#2C2731]/15 bg-[#F2EAC3] px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[#2C2731]">Platform Resource</p>
                  <p className="text-xs text-[#2C2731]/70">Make this resource available to all seekers on the platform.</p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-[#2C2731]">
                  <input
                    type="checkbox"
                    checked={editForm.admin_public}
                    onChange={event => handleEditFieldChange('admin_public', event.target.checked)}
                    disabled={editSaving}
                    className="h-4 w-4 accent-[#2C2731]"
                  />
                  Public
                </label>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-[#2C2731]/80" htmlFor="resource-file">
                  Replace file (optional)
                </label>
                <input
                  id="resource-file"
                  type="file"
                  onChange={(event) => setEditFile(event.target.files && event.target.files[0] ? event.target.files[0] : null)}
                  className="w-full text-sm text-[#2C2731]"
                  disabled={editSaving}
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4,.mp3,.wav"
                />
                <p className="mt-1 text-xs text-[#2C2731]/70">
                  Current file: {editingResource.file_name}. Leave empty to keep existing file.
                </p>
                {editFile && (
                  <p className="mt-1 text-xs text-[#2C2731]/70">
                    Selected: {editFile.name} ({formatFileSize(editFile.size)})
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3] disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450] disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={editSaving}
                >
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
